import * as Yup from "yup";
import type { FormikErrors } from "formik";
import type { CompanyRegistrationValues } from "../model/registrationTypes";
import { CompanyType, WhatToRegister } from "../model/registrationEnums";
import {
  advokatselskabSchema,
  interessentskabSchema,
  enkeltmandsvirksomhedSchema,
} from "../validation/creationValidation";
import {
  changeAdvokatselskabSchema,
  changeInteressentskabSchema,
} from "../validation/changeValidation";
import { terminationSchema } from "../validation/terminationValidation";
import { SELECT_REQUIRED_MESSAGE } from "../validation/registrationValidationShared";

const nestFlatErrors = (
  flat: Record<string, string>,
): FormikErrors<CompanyRegistrationValues> => {
  const out: Record<string, unknown> = {};

  for (const [key, message] of Object.entries(flat)) {
    const parts = key.replace(/\[(\d+)\]/g, ".$1").split(".");
    let current: Record<string, unknown> = out;

    parts.forEach((part, index) => {
      if (index === parts.length - 1) {
        current[part] = message;
        return;
      }

      current[part] = (current[part] as Record<string, unknown>) ?? {};
      current = current[part] as Record<string, unknown>;
    });
  }

  return out as FormikErrors<CompanyRegistrationValues>;
};

const mergePathErrors = (
  prefix: string,
  err: Yup.ValidationError,
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const inner = err.inner?.length ? err.inner : [err];

  for (const entry of inner) {
    const suffix = entry.path ?? "";
    const path = suffix ? `${prefix}.${suffix}` : prefix;

    if (!errors[path]) {
      errors[path] = entry.message;
    }
  }

  return errors;
};

const metaSchema = Yup.object({
  whatToRegister: Yup.string()
    .required(SELECT_REQUIRED_MESSAGE)
    .oneOf(
      [
        WhatToRegister.Creation,
        WhatToRegister.Change,
        WhatToRegister.Termination,
      ],
      SELECT_REQUIRED_MESSAGE,
    ),
  companyType: Yup.string()
    .required(SELECT_REQUIRED_MESSAGE)
    .when("whatToRegister", {
      is: WhatToRegister.Change,
      then: (schema) =>
        schema.oneOf(
          [CompanyType.Interessentskab, CompanyType.Advokatselskab],
          "Vælg interessentskab eller advokatselskab.",
        ),
      otherwise: (schema) =>
        schema.oneOf(
          [
            CompanyType.Enkeltmandsvirksomhed,
            CompanyType.Interessentskab,
            CompanyType.Advokatselskab,
          ],
          "Vælg virksomhedstype.",
        ),
    }),
});

const selectedFlowValidations = [
  {
    shouldValidate: (values: CompanyRegistrationValues) =>
      values.whatToRegister === WhatToRegister.Creation &&
      values.companyType === CompanyType.Enkeltmandsvirksomhed,
    prefix: "enkeltmandsvirksomhed",
    getValues: (values: CompanyRegistrationValues) =>
      values.enkeltmandsvirksomhed,
    schema: enkeltmandsvirksomhedSchema,
  },
  {
    shouldValidate: (values: CompanyRegistrationValues) =>
      values.whatToRegister === WhatToRegister.Creation &&
      values.companyType === CompanyType.Interessentskab,
    prefix: "interessentskab",
    getValues: (values: CompanyRegistrationValues) => values.interessentskab,
    schema: interessentskabSchema,
  },
  {
    shouldValidate: (values: CompanyRegistrationValues) =>
      values.whatToRegister === WhatToRegister.Creation &&
      values.companyType === CompanyType.Advokatselskab,
    prefix: "advokatselskab",
    getValues: (values: CompanyRegistrationValues) => values.advokatselskab,
    schema: advokatselskabSchema,
  },
  {
    shouldValidate: (values: CompanyRegistrationValues) =>
      values.whatToRegister === WhatToRegister.Change &&
      values.companyType === CompanyType.Interessentskab,
    prefix: "changeInteressentskab",
    getValues: (values: CompanyRegistrationValues) =>
      values.changeInteressentskab,
    schema: changeInteressentskabSchema,
  },
  {
    shouldValidate: (values: CompanyRegistrationValues) =>
      values.whatToRegister === WhatToRegister.Change &&
      values.companyType === CompanyType.Advokatselskab,
    prefix: "changeAdvokatselskab",
    getValues: (values: CompanyRegistrationValues) =>
      values.changeAdvokatselskab,
    schema: changeAdvokatselskabSchema,
  },
  {
    shouldValidate: (values: CompanyRegistrationValues) =>
      values.whatToRegister === WhatToRegister.Termination,
    prefix: "termination",
    getValues: (values: CompanyRegistrationValues) => values.termination,
    schema: terminationSchema,
  },
];

export async function validateCompanyRegistration(
  values: CompanyRegistrationValues,
): Promise<FormikErrors<CompanyRegistrationValues>> {
  const errors: Record<string, string> = {};

  try {
    await metaSchema.validate(
      {
        whatToRegister: values.whatToRegister,
        companyType: values.companyType,
      },
      { abortEarly: false },
    );
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      for (const inner of error.inner) {
        if (inner.path) {
          errors[inner.path] = inner.message;
        }
      }

      if (!Object.keys(errors).length && error.message) {
        errors.whatToRegister = error.message;
      }
    }

    return nestFlatErrors(errors);
  }

  for (const validation of selectedFlowValidations) {
    if (!validation.shouldValidate(values)) {
      continue;
    }

    try {
      await validation.schema.validate(validation.getValues(values), {
        abortEarly: false,
      });
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        Object.assign(errors, mergePathErrors(validation.prefix, error));
      }
    }
  }

  return nestFlatErrors(errors);
}
